import { useCallback, useRef, useState } from "react";

import { deckCounts, type DeckCounts } from "./note";
import { useCollectionChanged } from "./useCollectionChanged";
import { useRefreshOnFocus } from "./useRefreshOnFocus";

/**
 * `/api/decks/counts`, read once for the whole app.
 *
 * **It used to be read twice.** `App` wanted `dueToday` for the rail badge and `Read` wanted `new`
 * and `addedToday` for its top bar, and each called `deckCounts()` with no deck argument — the same
 * URL, the same response. Both hooks are live whenever Read is mounted, so every focus and every
 * job-finished event issued the identical request twice.
 *
 * That is worth more than a wasted round trip. `compute_deck_counts` is three `find_cards` plus a
 * `find_notes` plus `deck_due_tree()` over the whole deck, and it runs on the **single collection
 * worker thread** for the interactive handle — so the duplicate occupies the executor that every
 * other interactive read queues behind. On a real collection that is Read's own lookups waiting on
 * a number the app already had.
 *
 * One payload already carries every field both readers want, so this is de-duplication and not a
 * cache: `App` holds the hook and passes the numbers down.
 */
export function useDeckCounts(): {
  counts: DeckCounts | null;
  /** Move `new` and `addedToday` by `delta` right now, and discard any answer already in flight. */
  adjust: (delta: number) => void;
} {
  /**
   * Null, not zero, and one object rather than a field per reader.
   *
   * The two surfaces disagree about what an unreadable hub should render, and both are right:
   * the rail badge shows `0` because a badge of 0 beats a fabricated number, while Read's top bar
   * omits its counters entirely because a confident `0 added` after a morning's mining is a worse
   * answer than no answer. Holding the whole payload nullable lets each caller keep its own rule.
   */
  const [counts, setCounts] = useState<DeckCounts | null>(null);

  /**
   * The generation guard, and it is a WRITE-side invalidation token rather than fetch bookkeeping.
   *
   * `adjust` bumps it as its first act, so a response issued before an add or an undo resolves
   * stale and discards itself instead of clobbering the ±1 already on screen. That is 日本-203/204's
   * bug, and the guard has to travel with the fetch — which is the one thing to be careful about in
   * hoisting this out of `Read`.
   */
  const gen = useRef(0);

  const read = useCallback(() => {
    const issued = ++gen.current;
    void deckCounts()
      .then((fresh) => {
        if (issued !== gen.current) return;
        setCounts(fresh);
      })
      // Needs the hub. Every caller would rather keep the last known numbers than replace them
      // with zeroes, so a failure leaves the state exactly as it was.
      .catch(() => undefined);
  }, []);

  // Both halves of the job, in one place instead of duplicated across two components: focus covers
  // "you were away and studied in Anki desktop", the collection signal covers "something changed
  // under you" — a 03:00 job produces no focus event at all.
  useRefreshOnFocus(read);
  useCollectionChanged(read);

  const adjust = useCallback((delta: number) => {
    gen.current += 1;
    setCounts((current) => applyCountDelta(current, delta));
  }, []);

  return { counts, adjust };
}

/**
 * The optimistic ±1, as a pure function so the rules are pinned somewhere without a DOM.
 *
 * Three of them, and they were previously spelled inline at two call sites in `Read` in two
 * different shapes — `n + 1` going up against `Math.max(0, n - 1)` coming down:
 *
 * - **An unreadable hub stays unreadable.** Adding a card while the counters are absent must not
 *   invent a `1`; there is nothing to add to.
 * - **Neither counter goes below zero**, which is what the clamp was for. Applying it in both
 *   directions changes nothing — a count is never negative, so `n + 1` never trips it — and it
 *   removes the asymmetry that made the two call sites look like they disagreed.
 * - **The two counters move together**, because an add is one word and one new card.
 */
export function applyCountDelta(counts: DeckCounts | null, delta: number): DeckCounts | null {
  if (counts === null) return null;
  return {
    ...counts,
    new: Math.max(0, counts.new + delta),
    addedToday: Math.max(0, counts.addedToday + delta),
  };
}
